/**
 * StaffAppMobile — /staff-app entry.
 *
 * Iter-142 · Field Staff Mobile PWA Addendum.
 *
 * • Reuses AuthContext for session (no new auth logic).
 * • S2 role routing: agent → FieldAgentFlow, crew_leader / supervisor
 *   → LeadStaffMenu, manager (Regional Manager) → ManagerReadOnlyMenu.
 * • Sends non-staff sessions back to their appropriate dashboard.
 * • Renders the "Add to Home Screen" hint via the existing PWA
 *   install prompt path (the service worker already registers in
 *   src/index.js).
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useAppIcons } from '../lib/appIcons';
import FieldAgentFlow from '../components/staff-app/FieldAgentFlow';
import LeadStaffMenu from '../components/staff-app/LeadStaffMenu';
import ManagerReadOnlyMenu from '../components/staff-app/ManagerReadOnlyMenu';

const AGENT_ROLES = new Set(['agent', 'site_agent']);
const LEAD_ROLES = new Set(['supervisor', 'crew_leader']);
const RM_ROLES = new Set(['manager']);
const API = process.env.REACT_APP_BACKEND_URL;

export default function StaffAppMobile() {
  useAppIcons('staff');
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Iter-146 · FIX 3 — hidden `?qa=reset` action. When the query param
  // is present AND the current session is authorised, we POST to the
  // /api/qa/reset-staff-app-accounts endpoint. No visible UI.
  const qaResetRunRef = useRef(false);
  useEffect(() => {
    if (loading || !user) return;
    const params = new URLSearchParams(location.search || '');
    if (params.get('qa') !== 'reset' || qaResetRunRef.current) return;
    qaResetRunRef.current = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/qa/reset-staff-app-accounts`, {
          method: 'POST',
          credentials: 'include',
        });
        if (r.ok) {
          toast.success('QA state reset — 4 test accounts back to canonical.');
        } else if (r.status === 403) {
          toast.error('QA reset requires a test account or admin session.');
        } else {
          toast.error('QA reset failed.');
        }
      } catch {
        toast.error('QA reset failed (network).');
      } finally {
        // Strip the query param so the reset doesn't loop on the next
        // render pass or the next page mount.
        params.delete('qa');
        const suffix = params.toString();
        navigate(`/staff-app${suffix ? `?${suffix}` : ''}`, { replace: true });
      }
    })();
  }, [loading, user, location.search, navigate]);

  useEffect(() => {
    // Not authenticated → login (existing flow bounces back through
    // routeForUser which will already deep-link the user to the right
    // dashboard once /staff-app is a supported destination).
    if (!loading && !user) navigate('/staff-login?next=/staff-app', { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    // Non-staff roles → hand off to their own dashboard. Guard on
    // `!loading` so we never redirect off a stale-in-flight user.
    if (loading || !user) return;
    const role = (user.role || '').toLowerCase();
    if (role === 'property_manager') navigate('/property-manager-dashboard', { replace: true });
    else if (role === 'resident' || role === 'tenant') navigate('/resident', { replace: true });
    else if (role === 'admin' || role === 'company_manager') {
      // Executives get the full web command center — the mobile PWA is
      // execution-first for field staff. This mirrors the existing
      // routeForUser rules from LoginPage.
      navigate('/company-manager-dashboard', { replace: true });
    }
  }, [loading, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-500 grid place-items-center text-sm"
           data-testid="staff-app-loading">
        Loading…
      </div>
    );
  }
  if (!user) return null;

  const role = (user.role || '').toLowerCase();
  if (AGENT_ROLES.has(role)) return <FieldAgentFlow user={user} />;
  if (LEAD_ROLES.has(role)) return <LeadStaffMenu user={user} />;
  if (RM_ROLES.has(role)) return <ManagerReadOnlyMenu user={user} />;

  // Unknown role — fall back to lead menu (safest — every panel
  // enforces its own role visibility).
  return <LeadStaffMenu user={user} />;
}
